import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Drawer,
  Input,
  Modal,
  Space,
  Table,
  notification,
} from 'antd';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useGetMarketplaces } from '../../../../../utils/api/marketplaces/useGetMarketplaces';
import { useGetMarketplaceFetchCategories } from '../../../../../utils/api/mpCategories/useGetMarketplaceFetchCategories';
import { useSaveMarketplaceCategories } from '../../../../../utils/api/mpCategories/useSaveMarketplaceCategories';
import './styled.css';

const AddCategories = ({ open, mpCategories, onClose, marketplaceProp }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const { marketplace: marketplaceFromUrl } = useParams();
  const marketplace = marketplaceProp || marketplaceFromUrl;
  const [search, setSearch] = useState('');
  // For search-based marketplaces: the keyword actually sent to the backend
  // (only set when the user submits the search box).
  const [submittedKeyword, setSubmittedKeyword] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { marketplaces } = useGetMarketplaces();
  const currentMarketplace = marketplaces?.find(
    (mp) => mp.name === marketplace
  );
  const marketplaceId = currentMarketplace?.id;
  // Provided by the backend: true for marketplaces (Allegro, Amazon, ...)
  // whose categories can only be fetched by submitting a search keyword.
  const isSearchBased = !!currentMarketplace?.categories_are_search_based;

  const existingCategoryIds = useMemo(() => {
    if (!mpCategories) return new Set();
    return new Set(mpCategories.map((cat) => cat.name));
  }, [mpCategories]);

  const { fetchedCategories, isLoading, isFetching, isError, error } =
    useGetMarketplaceFetchCategories(
      {
        marketplace: marketplaceId,
        keyword: isSearchBased ? submittedKeyword : undefined,
      },
      {
        // Search-based marketplaces: only call the API once a keyword is
        // submitted. Others: fetch the whole tree as soon as we have an id.
        enabled:
          !!marketplaceId && (!isSearchBased || submittedKeyword.length > 0),
      }
    );

  const { saveCategories, isSaving } = useSaveMarketplaceCategories();

  const handleAdd = () => {
    Modal.confirm({
      title: "Confirmer l'ajout",
      content: `Voulez-vous vraiment ajouter ${selectedRowKeys.length} catégorie(s) ?`,
      okText: 'Ajouter',
      cancelText: 'Annuler',
      onOk: () => {
        saveCategories(
          { marketplaceId, categories: selectedRowKeys },
          {
            onSuccess: () => {
              notification.success({
                message: 'Catégories ajoutées avec succès !',
                placement: 'bottomRight',
                duration: 3,
              });
              setSelectedRowKeys([]);
              onClose();
            },
            onError: (error) => {
              notification.error({
                message: 'Erreur',
                description: `Erreur : ${error?.message || "Impossible d'ajouter les catégories"}`,
                placement: 'bottomRight',
                duration: 3,
              });
            },
          }
        );
      },
    });
  };

  const transformedCategories = useMemo(() => {
    if (!fetchedCategories || fetchedCategories.length === 0) return [];

    return fetchedCategories.map((cat) => {
      const parts = cat.id.split('|');
      return {
        key: cat.id,
        name: cat.name,
        category: parts[1] || '',
        categoryId: parts[0] || '',
        fullId: cat.id,
      };
    });
  }, [fetchedCategories]);

  const filteredCategories = useMemo(() => {
    // Search-based marketplaces are already filtered by the backend, so we
    // display the results as-is. Others are filtered client-side.
    if (isSearchBased || !search.trim()) return transformedCategories;
    const query = search.toLowerCase().trim();
    return transformedCategories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(query) ||
        cat.category.toLowerCase().includes(query)
    );
  }, [transformedCategories, search, isSearchBased]);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Catégorie',
      dataIndex: 'category',
      key: 'category',
    },
    {
      title: 'ID Catégorie',
      dataIndex: 'fullId',
      key: 'fullId',
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
    getCheckboxProps: (record) => ({
      disabled: existingCategoryIds.has(record.fullId),
    }),
  };

  return (
    <Drawer
      title="Ajouter des catégories"
      placement="right"
      onClose={onClose}
      open={open}
      width="calc(100vw - 220px)"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          loading={isSaving}
          disabled={selectedRowKeys.length === 0}
        >
          Ajouter ({selectedRowKeys.length})
        </Button>
      }
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <>
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {isError && (
              <Alert
                message="Erreur"
                description={
                  error?.message || 'Impossible de récupérer les catégories'
                }
                type="error"
                showIcon
              />
            )}
            {isSearchBased ? (
              <Input.Search
                placeholder="Rechercher une catégorie par mot-clé puis valider..."
                enterButton
                loading={isFetching}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onSearch={(value) => setSubmittedKeyword(value.trim())}
                allowClear
              />
            ) : (
              <Input
                placeholder="Rechercher une catégorie..."
                prefix={<SearchOutlined />}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
              />
            )}
            {isSearchBased && !submittedKeyword && !isFetching && (
              <Alert
                message="Saisissez un mot-clé puis validez pour rechercher des catégories."
                type="info"
                showIcon
              />
            )}
            <Table
              dataSource={filteredCategories}
              columns={columns}
              rowSelection={rowSelection}
              loading={isLoading || isFetching}
              size="small"
              scroll={{ y: 'calc(100vh - 300px)' }}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                size: 'small',
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100'],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} sur ${total} catégories`,
              }}
              rowClassName={(record) =>
                existingCategoryIds.has(record.fullId) ? 'row-disabled' : ''
              }
              onChange={(pagination) => {
                setCurrentPage(pagination.current);
                setPageSize(pagination.pageSize);
              }}
            />
          </Space>
        </>
      </Space>
    </Drawer>
  );
};

export default AddCategories;
